import { useState, useMemo, useEffect } from 'react';
import { ChevronDown, Building2, LayoutList, Search, RotateCcw, CheckCircle2 } from 'lucide-react';
import { Drawer } from 'vaul';
import { useProgramContext } from '../../context/ProgramContext';

function ContextSelector() {
  const {
    principals,
    programs,
    selectedPrincipalId,
    selectedProgramId,
    selectedPrincipal,
    selectedProgram,
    setSelectedPrincipalId,
    setSelectedProgramId,
    loadingPrincipals,
    loadingPrograms,
  } = useProgramContext();

  const [open, setOpen] = useState(false);
  const [principalQuery, setPrincipalQuery] = useState('');
  const [programQuery, setProgramQuery] = useState('');
  const [showAllMalls, setShowAllMalls] = useState(false);
  const [showAllPrograms, setShowAllPrograms] = useState(false);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('open-context-selector', onOpen);
    return () => window.removeEventListener('open-context-selector', onOpen);
  }, []);

  const needsSelection = !selectedPrincipalId || !selectedProgramId;

  const principalLabel =
    selectedPrincipal ? (selectedPrincipal as any).name ?? 'This mall' : 'Choose mall';
  const programLabel = selectedProgram
    ? selectedProgram.programName ?? (selectedProgram as any).displayName ?? 'This program'
    : 'Choose program';

  const selectedProgramImage =
    (selectedProgram as any)?.imageUrl ||
    (selectedProgram as any)?.programImage ||
    (selectedProgram as any)?.image ||
    null;

  const filteredPrincipals = useMemo(() => {
    const q = principalQuery.trim().toLowerCase();
    if (!q) return principals;
    return principals.filter((p) =>
      String((p as any).name ?? p.principalName ?? p.principalId).toLowerCase().includes(q),
    );
  }, [principalQuery, principals]);

  const filteredPrograms = useMemo(() => {
    const q = programQuery.trim().toLowerCase();
    if (!q) return programs;
    return programs.filter((p) =>
      String(p.programName ?? (p as any).displayName ?? p.programId).toLowerCase().includes(q),
    );
  }, [programQuery, programs]);

  const visiblePrincipals = showAllMalls ? filteredPrincipals : filteredPrincipals.slice(0, 8);
  const visiblePrograms = showAllPrograms ? filteredPrograms : filteredPrograms.slice(0, 8);

  let summaryText: string;
  if (!selectedPrincipalId && !selectedProgramId) {
    summaryText = 'Tap to choose mall & program';
  } else if (selectedPrincipalId && !selectedProgramId) {
    summaryText = `${principalLabel} - choose program`;
  } else {
    summaryText = `${principalLabel} - ${programLabel}`;
  }

  const handleSelectPrincipal = (principalId: number) => {
    setSelectedPrincipalId(principalId);
    setSelectedProgramId(null);
    setProgramQuery('');
    setShowAllPrograms(false);
  };

  const handleSelectProgram = (programId: number) => {
    setSelectedProgramId(programId);
    setOpen(false);
  };

  const handleCloseIfDone = () => {
    if (!needsSelection) {
      setOpen(false);
    }
  };

  const handleResetSelection = () => {
    setSelectedPrincipalId(null);
    setSelectedProgramId(null);
    setPrincipalQuery('');
    setProgramQuery('');
    setShowAllMalls(false);
    setShowAllPrograms(false);
  };

  if (loadingPrincipals && principals.length === 0) {
    return (
      <div className="flex items-center gap-3 px-4 py-3 text-sm text-gray-500 min-h-[52px] bg-white/90 backdrop-blur border-b border-slate-200/70">
        <div
          className="w-4 h-4 border-2 border-brand border-t-transparent rounded-full animate-spin"
          aria-hidden="true"
        />
        <span>Loading malls...</span>
      </div>
    );
  }

  if (principals.length === 0) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 text-sm text-gray-500 min-h-[52px] bg-white/90 backdrop-blur border-b border-slate-200/70">
        No malls are available for selection yet.
      </div>
    );
  }

  return (
    <Drawer.Root open={open} onOpenChange={setOpen}>
      <div
        className={`px-3 py-2.5 sm:px-4 sm:py-2 border-b ${
          needsSelection ? 'bg-brand/5 border-brand/25' : 'bg-white/85 border-slate-200/70 backdrop-blur'
        }`}
      >
        <div className="flex items-center justify-between gap-2 max-w-[980px] mx-auto">
          <div className="flex flex-col">
            <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
              Work area
            </span>
            {needsSelection ? (
              <span className="mt-0.5 text-[11px] text-slate-500 line-clamp-1">
                Choose a mall & program to start collecting VPAs.
              </span>
            ) : (
              <span className="mt-0.5 text-[11px] text-slate-500 line-clamp-1 flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5 text-brand" />
                Ready for VPA collection
              </span>
            )}
          </div>
          <Drawer.Trigger asChild>
            <button
              type="button"
              className={`flex-1 sm:flex-none inline-flex items-center justify-between gap-2 min-h-[40px] px-3 py-2 rounded-full border text-xs sm:text-sm transition-colors duration-150 shadow-soft ${
                needsSelection
                  ? 'border-brand/50 bg-white ring-1 ring-brand/20 text-brand'
                  : 'border-slate-200 bg-white text-slate-800'
              }`}
            >
              <span className="flex items-center gap-2 min-w-0">
                {selectedProgramImage ? (
                  <img
                    src={selectedProgramImage}
                    alt=""
                    className="w-6 h-6 rounded-md border border-gray-100 object-cover flex-shrink-0"
                  />
                ) : (
                  <Building2
                    className={`w-4 h-4 flex-shrink-0 ${
                      needsSelection ? 'text-brand' : 'text-slate-500'
                    }`}
                  />
                )}
                <span className="truncate text-left">{summaryText}</span>
              </span>
              <ChevronDown className="w-4 h-4 flex-shrink-0 text-slate-400" />
            </button>
          </Drawer.Trigger>
        </div>
      </div>

      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />
        <Drawer.Content className="fixed inset-x-0 bottom-0 z-[60] flex justify-center">
          <div
            className="w-full max-w-2xl flex flex-col bg-white rounded-t-2xl border border-slate-200 shadow-soft-lg max-h-[88vh]"
            style={{ paddingBottom: 'var(--safe-area-inset-bottom)' }}
          >
            <Drawer.Title className="sr-only">Select work area</Drawer.Title>
            <div className="mx-auto w-10 h-1.5 flex-shrink-0 rounded-full bg-slate-300 mt-3" />
            <div className="flex-1 overflow-auto p-4 pb-5 space-y-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold text-slate-900">Set where you are working</h2>
                  <p className="mt-1 text-xs text-slate-500">
                    Pick a mall first, then choose the program to start collection.
                  </p>
                  <p className="mt-2 text-[11px] text-slate-500">
                    {selectedPrincipalId ? 'Mall selected' : 'Step 1 pending'} - {selectedProgramId ? 'Program selected' : 'Step 2 pending'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleResetSelection}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 border border-slate-200 rounded-md hover:bg-slate-50"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Reset
                </button>
              </div>

              <section className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="flex items-center gap-2 text-xs font-semibold text-slate-600">
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand text-white text-[11px] font-semibold">
                      1
                    </span>
                    Choose mall
                  </h3>
                </div>
                <label className="relative block">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    value={principalQuery}
                    onChange={(e) => setPrincipalQuery(e.target.value)}
                    placeholder="Search mall..."
                    className="fc-input pl-9 text-xs"
                  />
                </label>
                <p className="text-[11px] text-slate-500 px-1">
                  Showing {visiblePrincipals.length} of {filteredPrincipals.length} mall
                  {filteredPrincipals.length === 1 ? '' : 's'}
                </p>
                <div className="space-y-1 max-h-52 overflow-auto fc-scroll-clean -mx-1 px-1">
                  {visiblePrincipals.map((p) => (
                    <button
                      key={p.principalId}
                      type="button"
                      onClick={() => handleSelectPrincipal(p.principalId)}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-md border text-xs text-left transition-colors duration-150 ${
                        p.principalId === selectedPrincipalId
                          ? 'border-brand bg-brand/5 text-brand font-medium'
                          : 'border-slate-200 bg-white text-slate-900'
                      }`}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <Building2
                          className={`w-4 h-4 flex-shrink-0 ${
                            p.principalId === selectedPrincipalId ? 'text-brand' : 'text-slate-400'
                          }`}
                        />
                        <span className="truncate">{(p as any).name ?? p.principalId}</span>
                      </span>
                      {p.principalId === selectedPrincipalId && (
                        <CheckCircle2 className="w-3.5 h-3.5 text-brand flex-shrink-0" />
                      )}
                    </button>
                  ))}
                  {filteredPrincipals.length === 0 && (
                    <p className="text-xs text-slate-500 py-3 px-2">No matching malls</p>
                  )}
                </div>
                {!showAllMalls && filteredPrincipals.length > visiblePrincipals.length && (
                  <div className="pt-1">
                    <button
                      type="button"
                      onClick={() => setShowAllMalls(true)}
                      className="text-[11px] font-semibold text-brand hover:text-brand-dark"
                    >
                      Show all {filteredPrincipals.length} malls
                    </button>
                  </div>
                )}
              </section>

              <section className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="flex items-center gap-2 text-xs font-semibold text-slate-600">
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand text-white text-[11px] font-semibold">
                      2
                    </span>
                    Choose program
                  </h3>
                </div>
                <label className="relative block">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    value={programQuery}
                    onChange={(e) => setProgramQuery(e.target.value)}
                    placeholder={selectedPrincipalId ? 'Search program...' : 'Select mall first'}
                    className="fc-input pl-9 text-xs"
                    disabled={!selectedPrincipalId}
                  />
                </label>
                {!selectedPrincipalId && (
                  <p className="text-xs text-slate-500">Select mall first to load programs.</p>
                )}
                {selectedPrincipalId && loadingPrograms && (
                  <p className="text-xs text-slate-500">Loading programs...</p>
                )}
                {selectedPrincipalId && !loadingPrograms && (
                  <>
                    <p className="text-[11px] text-slate-500 px-1">
                      Showing {visiblePrograms.length} of {filteredPrograms.length} program
                      {filteredPrograms.length === 1 ? '' : 's'}
                    </p>
                    {filteredPrograms.length === 0 && (
                      <p className="text-xs text-slate-500 py-2">
                        No programs found for this mall.
                      </p>
                    )}
                  </>
                )}
                {selectedPrincipalId && visiblePrograms.length > 0 && (
                  <div className="space-y-1 max-h-52 overflow-auto fc-scroll-clean -mx-1 px-1">
                    {visiblePrograms.map((p) => {
                      const programImage =
                        (p as any).imageUrl || (p as any).programImage || (p as any).image;
                      const isActive = p.programId === selectedProgramId;

                      return (
                        <button
                          key={p.programId}
                          type="button"
                          onClick={() => handleSelectProgram(p.programId)}
                          className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-md border text-xs text-left transition-colors duration-150 ${
                            isActive
                              ? 'border-brand bg-brand/5 text-brand font-medium'
                              : 'border-slate-200 bg-white text-slate-900'
                          }`}
                        >
                          <span className="flex items-center gap-2 min-w-0">
                            {programImage ? (
                              <img
                                src={programImage}
                                alt=""
                                className="w-7 h-7 rounded-md border border-gray-100 object-cover flex-shrink-0"
                              />
                            ) : (
                              <LayoutList
                                className={`w-4 h-4 flex-shrink-0 ${
                                  isActive ? 'text-brand' : 'text-slate-400'
                                }`}
                              />
                            )}
                            <span className="truncate">
                              {p.programName ?? (p as any).displayName ?? p.programId}
                            </span>
                          </span>
                          {isActive && (
                            <CheckCircle2 className="w-3.5 h-3.5 text-brand flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
                {selectedPrincipalId &&
                  !loadingPrograms &&
                  !showAllPrograms &&
                  filteredPrograms.length > visiblePrograms.length && (
                    <div className="pt-1">
                      <button
                        type="button"
                        onClick={() => setShowAllPrograms(true)}
                        className="text-[11px] font-semibold text-brand hover:text-brand-dark"
                      >
                        Show all {filteredPrograms.length} programs
                      </button>
                    </div>
                  )}
              </section>
            </div>
            <div className="border-t border-slate-100 px-4 py-3 flex justify-end">
              <button
                type="button"
                onClick={handleCloseIfDone}
                className="fc-btn-primary px-4 py-2 rounded-md text-xs sm:text-sm"
                disabled={needsSelection}
              >
                Start collecting VPAs
              </button>
            </div>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

export default ContextSelector;
